import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Search, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];

const lineReveal = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.15 + i * 0.14 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: -8 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE, delay: 0.7 + i * 0.08 },
  }),
};

const navLinkClass =
  "relative text-[11px] font-medium uppercase tracking-[0.24em] text-ink/80 transition-colors duration-300 hover:text-ink after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-ink after:transition-[width] after:duration-300 hover:after:w-full";

const soon = (what) => () => toast(`${what} is coming soon`);

function Brand({ compact = false }) {
  return (
    <div className="flex flex-col items-center" data-testid="header-brand">
      <div className="overflow-hidden">
        <motion.img
          src="/images/dachshund-mark.png"
          alt=""
          aria-hidden
          variants={lineReveal}
          custom={0}
          className={compact ? "h-4 w-auto" : "h-8 w-auto"}
        />
      </div>
      <div className="overflow-hidden">
        <motion.span
          variants={lineReveal}
          custom={1}
          className={
            compact
              ? "block whitespace-nowrap font-serif text-xl font-medium tracking-tight text-ink"
              : "block whitespace-nowrap font-serif text-[42px] font-medium leading-[1.15] tracking-tight text-ink xl:text-[52px]"
          }
        >
          My Dachshund Days
        </motion.span>
      </div>
    </div>
  );
}

export const Header = ({ onOpenNavRoom }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openRoom = (key) => () => {
    setMenuOpen(false);
    onOpenNavRoom(key);
  };

  const openContact = () => {
    setMenuOpen(false);
    if (location.pathname !== "/contact") navigate("/contact");
  };

  const openAbout = () => {
    setMenuOpen(false);
    if (location.pathname !== "/about") navigate("/about");
  };

  return (
    <motion.header
      initial="hidden"
      animate="show"
      className="relative mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-10"
    >
      {/* Mobile header */}
      <div className="flex items-center justify-between py-4 lg:hidden">
        <button
          type="button"
          data-testid="menu-button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
        <Brand compact />
        <div className="flex items-center">
          <button
            type="button"
            data-testid="nav-search-mobile"
            aria-label="Search"
            onClick={soon("Search")}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5"
          >
            <Search className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            data-testid="nav-cart-mobile"
            aria-label="Cart"
            onClick={soon("Cart")}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5"
          >
            <ShoppingBag className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden lg:hidden"
          >
            <div className="flex flex-col gap-1 pb-5 pt-1">
              <button type="button" data-testid="nav-home-mobile" onClick={goHome} className="rounded-2xl px-4 py-3.5 text-left text-sm font-medium uppercase tracking-[0.22em] text-ink transition-colors hover:bg-ink/5">
                Home
              </button>
              <button type="button" data-testid="nav-about-mobile" onClick={openAbout} className="rounded-2xl px-4 py-3.5 text-left text-sm font-medium uppercase tracking-[0.22em] text-ink/75 transition-colors hover:bg-ink/5">
                About
              </button>
              <button type="button" data-testid="nav-contact-mobile" onClick={openContact} className="rounded-2xl px-4 py-3.5 text-left text-sm font-medium uppercase tracking-[0.22em] text-ink/75 transition-colors hover:bg-ink/5">
                Contact
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Desktop header */}
      <div className="hidden grid-cols-[1fr_auto_1fr] items-end gap-8 pt-10 lg:grid">
        <motion.nav variants={fade} custom={0} className="flex items-center gap-9 pb-4" aria-label="Primary">
          <button type="button" data-testid="nav-home" onClick={goHome} className={navLinkClass}>
            Home
          </button>
          <button type="button" data-testid="nav-about" onClick={openAbout} className={navLinkClass}>
            About
          </button>
        </motion.nav>

        <div className="flex items-end gap-6">
          <motion.img
            src="/images/sprig-left.png"
            alt=""
            aria-hidden
            variants={fade}
            custom={1}
            className="hidden h-16 w-auto pb-1 xl:block"
          />
          <Brand />
          <motion.img
            src="/images/sprig-right.png"
            alt=""
            aria-hidden
            variants={fade}
            custom={2}
            className="hidden h-16 w-auto pb-1 xl:block"
          />
        </div>

        <motion.div variants={fade} custom={3} className="flex items-center justify-end gap-2 pb-4">
          <button type="button" data-testid="nav-contact" onClick={openContact} className={`${navLinkClass} mr-5`}>
            Contact
          </button>
          <button
            type="button"
            data-testid="nav-search"
            aria-label="Search"
            onClick={soon("Search")}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5"
          >
            <Search className="h-[19px] w-[19px]" />
          </button>
          <button
            type="button"
            data-testid="nav-cart"
            aria-label="Cart"
            onClick={soon("Cart")}
            className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5"
          >
            <ShoppingBag className="h-[19px] w-[19px]" />
          </button>
        </motion.div>
      </div>

      <div className="overflow-hidden pb-8 pt-2 text-center lg:pb-10 lg:pt-3">
        <motion.p
          data-testid="brand-tagline"
          variants={lineReveal}
          custom={2}
          className="text-[10px] font-medium uppercase tracking-[0.42em] text-ink/70 lg:text-[11px]"
        >
          Little dogs. Big inspiration.
        </motion.p>
      </div>
    </motion.header>
  );
};
